import React, { Fragment, useMemo } from 'react';
import { motion } from 'framer-motion';
import { formatNumber } from '../../../../../Utils';
import { parseMoney } from './pmt.js';

function formatMoney(n) {
    if (!Number.isFinite(n)) return '—';
    return `$${formatNumber(n.toFixed(2))}`;
}

const IVA = 0.16;

const PURO_ROWS = [
    { key: 'Gasto Inicial Arrendamiento', label: 'GASTO INICIAL DE ARRENDAMIENTO', hasIva: true },
    { key: 'Comisión Apertura', label: 'COMISIÓN POR APERTURA', hasIva: true },
    { key: 'Gastos de Originación', label: 'GASTOS DE ORIGINACIÓN', hasIva: true },
    { key: 'Renta Anticipada', label: 'RENTA ANTICIPADA', hasIva: true, optional: true },
];

const FINANCIERO_ROWS = [
    { key: 'Enganche', label: 'ENGANCHE', hasIva: true },
    { key: 'Comisión por Apertura', label: 'COMISIÓN POR APERTURA', hasIva: true },
    { key: 'Gastos de Originación', label: 'GASTOS DE ORIGINACIÓN', hasIva: true },
];

function rowValues(plazo, row) {
    const raw = plazo?.section2?.[row.key];
    if (!raw) return null;
    const sinIva = parseMoney(raw[1]);
    const conIva = row.hasIva ? sinIva * (1 + IVA) : sinIva;
    return { sinIva, conIva };
}

function totalValues(plazo) {
    const section2 = plazo?.section2 || {};
    const sinIva = parseMoney(section2['Total Pago Inicial (sin IVA)']?.[0]);
    const conIva = parseMoney(
        section2['Total Pago Inicial (incl IVA)']?.[1] ||
        section2['Total Pago Inicial (incl IVA)']?.[0]
    );
    return { sinIva, conIva };
}

function InitialPaymentTable({ state, isCotizador3 = false }) {
    const { rows, plazos, isPure, expanded } = useMemo(() => {
        const all = (state?.pdfPlazo || [])
            .filter(p => !p.ignore)
            .slice()
            .sort((a, b) => a.plazo - b.plazo);
        const selected = all.find(p => p.selected) || all[0];
        if (!selected) return { rows: [], plazos: [], isPure: false, expanded: false };

        const section2 = selected.section2 || {};
        const isPureSection = 'Gasto Inicial Arrendamiento' in section2;
        const schema = isPureSection ? PURO_ROWS : FINANCIERO_ROWS;

        const filteredRows = schema.filter(r => {
            const found = all.some(p => {
                const values = rowValues(p, r);
                if (!values) return false;
                if (r.optional && values.sinIva === 0) return false;
                return true;
            });
            return found;
        });

        const expanded = isCotizador3 && all.length > 1;
        const plazos = expanded ? all : [selected];

        return { rows: filteredRows, plazos, isPure: isPureSection, expanded };
    }, [state, isCotizador3]);

    if (!rows.length || !plazos.length) return null;

    const subtitle = expanded
        ? 'valores estimados, pueden cambiar'
        : (isPure ? 'mismo para todos los plazos' : 'arrendamiento financiero');

    return (
        <div className={`InitialPayment${expanded ? ' InitialPayment--expanded' : ''}`}>
            <div className="MatrixHeader">
                <div className="MatrixTitle">
                    <span className="Dot amber" />
                    <h3>Desglose pago inicial</h3>
                    <span className="Subtitle">{subtitle}</span>
                </div>
            </div>

            <div className="InitialTableWrapper">
                <table className="InitialTable">
                    <thead>
                        {expanded ? (
                            <tr>
                                <th />
                                {plazos.map(p => (
                                    <th
                                        key={p.plazo}
                                        className={`plazo-col-head money${p.selected ? ' current-col' : ''}`}
                                    >
                                        {p.plazo} meses
                                    </th>
                                ))}
                            </tr>
                        ) : (
                            <tr>
                                <th />
                                <th className="money">S/IVA</th>
                                <th className="money">C/IVA</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <motion.tr
                                key={row.label}
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05, duration: 0.3 }}
                            >
                                <td className="label-cell">{row.label}</td>
                                {expanded
                                    ? plazos.map(p => {
                                        const values = rowValues(p, row) || { sinIva: 0, conIva: 0 };
                                        return (
                                            <td key={p.plazo} className="money">
                                                {formatMoney(values.conIva)}
                                            </td>
                                        );
                                    })
                                    : (() => {
                                        const values = rowValues(plazos[0], row) || { sinIva: 0, conIva: 0 };
                                        return (
                                            <Fragment>
                                                <td className="money">{formatMoney(values.sinIva)}</td>
                                                <td className="money">{formatMoney(values.conIva)}</td>
                                            </Fragment>
                                        );
                                    })()}
                            </motion.tr>
                        ))}
                        <motion.tr
                            className="total-row"
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (rows.length + 1) * 0.05, duration: 0.35 }}
                        >
                            <td className="label-cell total-label">TOTAL PAGO INICIAL</td>
                            {expanded
                                ? plazos.map(p => {
                                    const totals = totalValues(p);
                                    return (
                                        <td key={p.plazo} className="money total-amount">
                                            {formatMoney(totals.conIva)}
                                        </td>
                                    );
                                })
                                : (() => {
                                    const totals = totalValues(plazos[0]);
                                    return (
                                        <Fragment>
                                            <td className="money">{formatMoney(totals.sinIva)}</td>
                                            <td className="money total-amount">{formatMoney(totals.conIva)}</td>
                                        </Fragment>
                                    );
                                })()}
                        </motion.tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export { InitialPaymentTable };
